/**
 * AppSidebar — ERP/HR 통합 사이드바 (2026-10-14 워크스페이스 디자인, 레퍼런스 «Tendra»)
 *
 * 구조 (위 → 아래) — 회색 바탕 위에 바로 놓인다(흰 판 없음)
 *  1) 브랜드(검정 타일 + MARYHOUSE) · 접기 단추
 *  2) ERP ↔ HR 전환 — SystemSwitch (브랜드와 겹치던 «MH 메리하우스» 칸은 2026-10-15 제거)
 *  3) 검색(⌘K 팔레트)
 *  4) 메뉴 — 섹션 이름(고정폭 작은 글씨, 눌러서 접기) 아래 항목을 펼쳐 둔다. 지금 화면은 흰 알약.
 *  5) 관리 — 알림 · 회사 관리 · 내 설정
 *  6) 바닥 — 상태 카드(인박스 미처리) + 프로필(설정·로그아웃)
 *
 * 권한·배지 계산은 예전 그대로다(utils/menuAccess 정본 · 인박스 이벤트 → 메뉴 매핑).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Activity, AlertOctagon, Banknote, Bell, BookOpen, Bot, Building2, CalendarDays, ChevronRight, ClipboardList, Clock,
  DoorOpen, Download, Ellipsis, Factory, FileBarChart, FileCheck, FileSignature, FileText, Gift, GraduationCap,
  LayoutGrid, LineChart, ListChecks, LogOut, Megaphone, MessagesSquare, Mic, Network, Package, PanelLeft, Phone,
  PieChart, Plane, Search, Settings, Shield, ShieldCheck, ShoppingCart, Sofa, SquareKanban, Star, Target, TrendingUp,
  UserRound, Users, Wallet, Workflow, Wrench, Dot, type LucideIcon,
} from 'lucide-react';
import { Button } from '../ui/shadcn/button';
import { useAuth } from '../../context/AuthContext';
import { useSystemMode } from '../../context/SystemModeContext';
import { menuSections, MenuSection } from '../../constants/menuData';
import { hrMenuSections } from '../../constants/hrMenuData';
import { canAccessMenu } from '../../utils/menuAccess';
import { roleLabel } from '../../utils/hrLabels';
import { useBadgeCounts } from '../../hooks/hr/useBadgeCounts';
import { useAdminInboxCounts } from '../../hooks/useAdminInboxCounts';
import { useUnmappedCampaignCount } from '../../hooks/useUnmappedCampaignCount';
import mhSymbol from '../../assets/mh-symbol-grey.png';
import SystemSwitch from './SystemSwitch';
import { WsAvatar } from '../ws';

export const SIDEBAR_WIDTH = 268;
export const SIDEBAR_COLLAPSED_WIDTH = 72;

/** 메뉴 key → 선 아이콘. 없으면 점 */
const ICONS: Record<string, LucideIcon> = {
  dashboard: LayoutGrid, wiki: BookOpen, 'ontology-chat': Bot, 'ontology-monitoring': Activity, 'mcp-approvals': ShieldCheck,
  'meeting-notes': Mic, contacts: Users, inventory: Package, sales: TrendingUp, purchase: ShoppingCart, production: Factory,
  bom: Network, happycall: Phone, 'home-styling': Sofa, 'installation-scheduling': CalendarDays, claims: AlertOctagon,
  'repair-requests': Wrench, 'marketing-ad': Megaphone, 'marketing-channel-pnl': PieChart, 'automation-orders': Download,
  'automation-tasks': Workflow,
  'hr-me': UserRound, 'hr-dashboard': LayoutGrid, 'hr-todo': ListChecks, 'hr-search': Search, 'hr-org': Network,
  'hr-responsibility': ClipboardList, 'hr-my-attendance': Clock, 'hr-my-status': Wallet, 'hr-team-attendance': Users,
  'hr-team-schedule': CalendarDays, 'hr-leaves': Plane, 'hr-approvals': FileCheck, 'hr-onboarding': DoorOpen,
  'hr-contracts': FileSignature, 'hr-documents': FileText, 'hr-goals': Target, 'hr-reviews': Star, 'hr-tasks': SquareKanban,
  'hr-education': GraduationCap, 'hr-meetings': MessagesSquare, 'hr-payroll': Banknote, 'hr-benefits': Gift,
  'hr-insurance': Shield, 'hr-trend': LineChart, 'hr-reports': FileBarChart,
};
const iconFor = (key: string, size = 18) => {
  const I = ICONS[key] || Dot;
  return <I size={size} strokeWidth={1.7} />;
};

// ─────── 공통 Nav 트리 (ERP/HR 양쪽) — 섹션 하나 = 이름 + 항목들 ───────
interface NavLink { label: string; path: string; key: string }
interface NavSection { id: string; label: string | null; items: NavLink[] }

const toErpSections = (sections: MenuSection[]): NavSection[] => sections.map((section) => ({
  id: section.sidebarTitle,
  label: section.sidebarTitle,
  // 감춘 메뉴는 사이드바에 넣지 않는다. 주소·권한은 그대로다 (menuData 의 hiddenInSidebar 주석 참조)
  items: section.menus.filter((m) => !m.hiddenInSidebar).map((m) => ({ label: m.title, path: m.path, key: m.key })),
}));

const toHrSections = (isAdmin: boolean): NavSection[] => {
  const top: NavSection = { id: 'hr-top', label: '내 업무', items: [] };
  const result: NavSection[] = [top];
  for (const entry of hrMenuSections) {
    if (entry.type === 'item') {
      // Sprint 47-A: top-level item 도 requiresAdmin 지원
      if (entry.requiresAdmin && !isAdmin) continue;
      top.items.push({ label: entry.title, path: entry.path, key: entry.key });
      continue;
    }
    // Sprint 7B-12A: requiresAdmin=true 메뉴는 admin/superadmin 만 노출
    const visibleChildren = entry.children.filter((c) => !c.requiresAdmin || isAdmin);
    result.push({ id: entry.id, label: entry.title, items: visibleChildren.map((c) => ({ label: c.title, path: c.path, key: c.key })) });
  }
  return result.filter((s) => s.items.length > 0);
};

const CLOSED_KEY = 'ws-nav-closed-sections';
function readClosed(): Set<string> {
  try {
    const arr = JSON.parse(window.localStorage.getItem(CLOSED_KEY) || '[]');
    return new Set(Array.isArray(arr) ? arr.filter((x) => typeof x === 'string') : []);
  } catch { return new Set(); }
}

const Count: React.FC<{ n: number; tone?: 'red' | 'orange' }> = ({ n, tone = 'orange' }) => (
  n > 0 ? <span className={`ws-nav-badge ws-tone-${tone}`}>{n > 99 ? '99+' : n}</span> : null
);

// ─────────── Component ───────────
interface Props {
  collapsed: boolean;
  onToggleCollapse: () => void;
}

const AppSidebar: React.FC<Props> = ({ collapsed, onToggleCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, logout } = useAuth();
  const { mode } = useSystemMode();
  const isAdmin = ['admin', 'superadmin'].includes(state.user?.role || '');
  // Sprint 7B-7 → 7B-8: HR 모드일 때 짧은 폴링 + visibility 훅 + BroadcastChannel
  const { counts: hrCounts } = useBadgeCounts(mode === 'hr' ? 20_000 : 600_000);
  // 어드민 인박스 — HR 모드에서도 동작(헤더 종 카운트). 일반 모드면 메뉴 배지에도 노출.
  const { counts: inboxCounts } = useAdminInboxCounts(20_000);

  // 미매핑 광고 캠페인 배지 [C-3] — ERP 모드 + marketing-ad view 권한 시에만 조회.
  // 권한 판정 기준은 아래 hasMenuPermission 과 동일(utils/menuAccess 정본).
  const canViewMarketingAd = useMemo(
    (): boolean => canAccessMenu(state.user, 'marketing-ad'),
    [state.user],
  );
  const unmappedEnabled = mode !== 'hr' && canViewMarketingAd;
  const { count: unmappedAdCount } = useUnmappedCampaignCount(unmappedEnabled);

  // 인박스 — 이벤트 키 → 메뉴 키 매핑
  const inboxForMenuKey = (menuKey: string): number => {
    if (!isAdmin) return 0;
    if (menuKey === 'installation-scheduling') {
      return (
        (inboxCounts.byEventKey['install.request.create'] || 0) +
        (inboxCounts.byEventKey['install.schedule.change_request'] || 0) +
        (inboxCounts.byEventKey['install.request.cancel'] || 0)
      );
    }
    if (menuKey === 'home-styling') {
      return inboxCounts.byEventKey['home_styling.create'] || 0;
    }
    if (menuKey === 'repair-requests') {
      return inboxCounts.byEventKey['repair_request.create'] || 0;
    }
    return 0;
  };
  const badgeForMenu = (key: string): number => {
    const inboxN = inboxForMenuKey(key);
    if (mode !== 'hr') {
      // ERP 모드: 광고성과 메뉴 = 미매핑 캠페인 수 배지
      if (key === 'marketing-ad') return unmappedAdCount;
      return inboxN;
    }
    if (key === 'hr-approvals') return hrCounts.approvals_incoming || 0;
    if (key === 'hr-leaves') return hrCounts.leaves_pending || 0;
    // [D-5] 답해야 할 업무 요청. 슬랙을 끈 사람에게는 여기 말고 알려 줄 자리가 없다.
    if (key === 'hr-todo') return hrCounts.task_requests_incoming || 0;
    return inboxN;
  };

  // 권한 체크 (ERP 메뉴에만 적용, HR 메뉴는 Phase 1에서 전체 허용)
  // 판정은 utils/menuAccess 정본 — 라우트 가드(MenuRoute)·백엔드와 같은 규칙을 쓴다.
  const hasMenuPermission = useCallback(
    (menuKey: string): boolean => canAccessMenu(state.user, menuKey),
    [state.user],
  );

  const sections: NavSection[] = useMemo(() => {
    if (mode === 'hr') return toHrSections(isAdmin);
    return toErpSections(menuSections)
      .map((s) => ({ ...s, items: s.items.filter((c) => hasMenuPermission(c.key)) }))
      .filter((s) => s.items.length > 0);
  }, [mode, hasMenuPermission, isAdmin]);

  const isPathActive = useCallback(
    (path: string) => location.pathname === path || location.pathname.startsWith(path + '/'),
    [location.pathname],
  );

  // 접어 둔 섹션 — 브라우저에 기억. 지금 화면이 든 섹션은 늘 펼친다
  const [closed, setClosed] = useState<Set<string>>(() => readClosed());
  const toggleSection = (id: string) => setClosed((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    try { window.localStorage.setItem(CLOSED_KEY, JSON.stringify(Array.from(next))); } catch { /* 기억 못 해도 동작 */ }
    return next;
  });

  // 프로필 메뉴
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!profileOpen) return undefined;
    const h = (e: MouseEvent) => { if (profileRef.current && !profileRef.current.contains(e.target as Node)) setProfileOpen(false); };
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') setProfileOpen(false); };
    document.addEventListener('mousedown', h);
    document.addEventListener('keydown', k);
    return () => { document.removeEventListener('mousedown', h); document.removeEventListener('keydown', k); };
  }, [profileOpen]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const openPalette = () => {
    // ⌘K 키 이벤트로 SimpleLayout 의 팔레트를 연다. ⛔ window 가 아니라 body 에서 보낸다 —
    //    다른 단축키 처리기가 e.target 을 화면 요소로 여기고 getAttribute 를 부른다(window 면 오류). window 까지는 그대로 올라간다
    document.body.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true, ctrlKey: true, bubbles: true }));
  };

  // 알림 — 어드민 인박스 미처리 우선, 없으면 HR 안읽음
  const inboxUnresolved = inboxCounts.unresolved || 0;
  const hrUnread = hrCounts.notifications_unread || 0;
  const notifCount = inboxUnresolved > 0 ? inboxUnresolved : hrUnread;
  const notifPath = mode === 'hr' ? '/hr/notifications' : '/notifications';

  // 함수로 그린다 — 컴포넌트로 두면 매번 새 종류가 되어 다시 붙는다(포커스가 날아간다)
  const navItem = ({ id, label, icon, active, onClick, aside, dot }: {
    id: string; label: string; icon: React.ReactNode; active?: boolean; onClick: () => void; aside?: React.ReactNode; dot?: boolean;
  }) => (
    <Button
      key={id}
      type="button"
      variant="ghost"
      className="ws-nav-item"
      aria-current={active ? 'page' : undefined}
      aria-label={label}
      title={collapsed ? label : undefined}
      onClick={onClick}
    >
      {icon}
      {!collapsed && <span className="ws-truncate" style={{ flex: 1 }}>{label}</span>}
      {!collapsed && aside}
      {collapsed && dot && <span className="ws-nav-dot" aria-hidden />}
    </Button>
  );

  const roleText = state.user?.role ? roleLabel(state.user.role) : '';

  return (
    <aside className="ws-sidebar" data-collapsed={collapsed ? 'true' : undefined} aria-label="주 메뉴">
      {/* 1) 브랜드 */}
      <div className="ws-brand">
        <Button type="button" variant="ghost" className="ws-brand-link" onClick={() => navigate(mode === 'hr' ? '/hr/me' : '/dashboard')} aria-label="MARYHOUSE 홈">
          <span className="ws-brand-tile"><img src={mhSymbol} alt="" /></span>
          {!collapsed && <span>MARYHOUSE</span>}
        </Button>
        <Button
          type="button"
          variant="ghost"
          className="ws-btn ws-btn--icon ws-btn--sm"
          onClick={onToggleCollapse}
          aria-label={collapsed ? '사이드바 펼치기' : '사이드바 접기'}
          title={collapsed ? '펼치기' : '접기'}
        >
          <PanelLeft size={16} strokeWidth={1.7} />
        </Button>
      </div>

      {/* 2) ERP ↔ HR 전환 */}
      <SystemSwitch collapsed={collapsed} />

      {/* 3) 검색 */}
      {collapsed ? (
        <Button type="button" variant="ghost" className="ws-btn ws-btn--icon" onClick={openPalette} aria-label="검색 (⌘K)" title="검색 (⌘K)" style={{ alignSelf: 'center' }}>
          <Search size={17} strokeWidth={1.7} />
        </Button>
      ) : (
        <Button type="button" variant="ghost" className="ws-search" onClick={openPalette} aria-label="검색 (⌘K)">
          <Search size={17} strokeWidth={1.7} />
          검색
          <span className="ws-kbd">⌘K</span>
        </Button>
      )}

      {/* 4) 메뉴 */}
      <nav className="ws-nav">
        {sections.length === 0 && !collapsed && (
          <div className="ws-sub" style={{ padding: '24px 12px', textAlign: 'center' }}>표시할 메뉴가 없습니다.</div>
        )}
        {sections.map((sec) => {
          const hasActive = sec.items.some((c) => isPathActive(c.path));
          const open = collapsed || hasActive || !closed.has(sec.id);
          return (
            <div key={sec.id} role="group" aria-label={sec.label || undefined}>
              {collapsed ? (
                <div style={{ height: 1, background: 'var(--ws-line)', margin: '10px 14px' }} aria-hidden />
              ) : sec.label && (
                <Button
                  type="button"
                  variant="ghost"
                  className="ws-nav-label"
                  aria-expanded={open}
                  onClick={() => !hasActive && toggleSection(sec.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, width: '100%', height: 'auto', justifyContent: 'flex-start', background: 'transparent', borderRadius: 6 }}
                >
                  <span className="ws-truncate">{sec.label}</span>
                  {!hasActive && <ChevronRight size={12} className="ws-nav-caret" data-open={open ? 'true' : undefined} style={{ marginLeft: 'auto' }} />}
                </Button>
              )}
              {open && sec.items.map((c) => {
                const badge = badgeForMenu(c.key);
                return navItem({
                  id: c.key,
                  label: c.label,
                  icon: iconFor(c.key),
                  active: isPathActive(c.path),
                  onClick: () => navigate(c.path),
                  aside: <Count n={badge} />,
                  dot: badge > 0,
                });
              })}
            </div>
          );
        })}

        {/* 5) 관리 */}
        {collapsed ? <div style={{ height: 1, background: 'var(--ws-line)', margin: '10px 14px' }} aria-hidden /> : <span className="ws-nav-label">관리</span>}
        {navItem({
          id: 'notifications',
          label: '알림',
          icon: <Bell size={18} strokeWidth={1.7} />,
          active: location.pathname === '/hr/notifications' || location.pathname === '/notifications',
          onClick: () => navigate(notifPath),
          // 인박스 미처리는 위험(red), HR 안읽음은 강조(orange) — 의미 분리
          aside: <Count n={notifCount} tone={inboxUnresolved > 0 ? 'red' : 'orange'} />,
          dot: notifCount > 0,
        })}
        {isAdmin && navItem({
            id: 'manage',
            label: '회사 관리',
            icon: <Building2 size={18} strokeWidth={1.7} />,
            active: location.pathname.startsWith('/manage'),
            /* 「회사 관리」는 ERP 모드·HR 모드 어느 쪽에서 눌러도 같은 곳으로 간다.
               예전에는 모드에 따라 /admin 과 /hr/admin/settings 로 갈라져,
               같은 버튼이 서로 다른 화면을 열었다 — «아까 본 설정이 어디 갔지»의 원인.
               목적지를 /hr 밖으로 두는 것도 중요하다. SystemModeContext 는 주소가 /hr 로
               시작하면 앱 전체를 HR 모드로 바꾸고 ERP 로는 되돌리지 않는다. */
            onClick: () => navigate('/manage/people/accounts'),
        })}
        {navItem({
          id: 'settings',
          label: '내 설정',
          icon: <Settings size={18} strokeWidth={1.7} />,
          active: location.pathname.startsWith('/settings'),
          onClick: () => navigate('/settings/user'),
        })}
      </nav>

      {/* 6) 바닥 — 상태 카드 + 프로필 */}
      {!collapsed && (
        <Button
          type="button"
          variant="ghost"
          className="ws-status-card"
          onClick={() => navigate(mode === 'hr' ? '/hr/approvals?scope=incoming' : '/notifications?tab=unresolved')}
        >
          {mode === 'hr' ? (
            <>
              <span className={`ws-status-title ${(hrCounts.approvals_incoming || 0) > 0 ? 'ws-tone-amber' : 'ws-tone-green'}`}>
                <span className="ws-live-dot" />
                {(hrCounts.approvals_incoming || 0) > 0 ? `결재 차례 ${hrCounts.approvals_incoming}건` : '결재 차례 없음'}
              </span>
              <span className="ws-status-sub">휴가 승인 대기 {hrCounts.leaves_pending || 0}건 · 업무 요청 {hrCounts.task_requests_incoming || 0}건</span>
            </>
          ) : (
            <>
              <span className={`ws-status-title ${inboxUnresolved > 0 ? 'ws-tone-red' : 'ws-tone-green'}`}>
                <span className="ws-live-dot" />
                {inboxUnresolved > 0 ? `인박스 미처리 ${inboxUnresolved}건` : '인박스 모두 처리됨'}
              </span>
              <span className="ws-status-sub">고객 신청·설치 변경 요청을 20초마다 확인합니다</span>
            </>
          )}
        </Button>
      )}

      <div ref={profileRef} style={{ position: 'relative' }}>
        <Button
          type="button"
          variant="ghost"
          className="ws-profile"
          aria-haspopup="menu"
          aria-expanded={profileOpen}
          aria-label="내 계정 메뉴"
          onClick={() => setProfileOpen((v) => !v)}
          style={collapsed ? { justifyContent: 'center', padding: 4 } : undefined}
        >
          <WsAvatar name={state.user?.name || state.user?.email || '사용자'} size="lg" />
          {!collapsed && (
            <>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span className="ws-profile-name ws-truncate">{state.user?.name || '사용자'}</span>
                <span className="ws-profile-role ws-truncate">{roleText || state.user?.email}</span>
              </span>
              <Ellipsis size={17} style={{ color: 'var(--ws-ink-4)' }} aria-hidden />
            </>
          )}
        </Button>
        {profileOpen && (
          <div className="ws-menu" role="menu" style={{ bottom: 'calc(100% + 6px)', left: 0, right: collapsed ? 'auto' : 0 }}>
            <Button type="button" variant="ghost" role="menuitem" className="ws-menu-item" onClick={() => { setProfileOpen(false); navigate('/settings/user'); }}>
              <Settings size={15} /> 내 설정
            </Button>
            <Button type="button" variant="ghost" role="menuitem" className="ws-menu-item" data-danger="true" onClick={() => { setProfileOpen(false); handleLogout(); }}>
              <LogOut size={15} /> 로그아웃
            </Button>
          </div>
        )}
      </div>
    </aside>
  );
};

export default AppSidebar;
